import React, { useCallback, useEffect, useState } from "react";
import TreeView from "./views/tree/TreeView/TreeView";
import SchedulePage from "./views/schedule/SchedulePage/SchedulePage";
import PullPlanPage from "./views/pull-plan/PullPlanPage/PullPlanPage";
import TaktBoardPage from "./views/takt/TaktBoardPage/TaktBoardPage";
import ExportPage from "./views/export/ExportPage/ExportPage";
import UpdateScheduleHost from "./views/updateSchedule/UpdateScheduleHost/UpdateScheduleHost";
import AdminPage from "./auth/AdminPage/AdminPage";
import LoginPage from "./auth/LoginPage/LoginPage";
import AcceptInvitePage from "./auth/AcceptInvitePage/AcceptInvitePage";
import ViewTabs, { ViewMode } from "./components/ViewTabs/ViewTabs";
import DbSwitcher from "./components/DbSwitcher/DbSwitcher";
import ProjectDeltaChip from "./components/ProjectDeltaChip/ProjectDeltaChip";
import UpdateScheduleBadge from "./components/UpdateScheduleBadge/UpdateScheduleBadge";
import TenantSwitcher from "./auth/TenantSwitcher/TenantSwitcher";
import { useAuth } from "./auth/AuthContext/AuthContext";
import Notice from "./components/Notice/Notice";
import {
  fetchDbVersions,
  fetchActiveVersion,
  setActiveVersion as setActiveVersionApi,
  trackEvent,
} from "./api/clientV2";
import type { DbVersionInfo, PullPlanSeed } from "./models";
import "./styles/global.css";

function App() {
  const { user, loading, logout } = useAuth();
  const [activeView, setActiveView] = useState<ViewMode>("takt");
  const [versions, setVersions] = useState<DbVersionInfo[]>([]);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [pullPlanSeed, setPullPlanSeed] = useState<PullPlanSeed | null>(null);
  // Update Schedule opens over WHATEVER view is mounted (an app-level
  // overlay), so the badge/button never yanks the user to the Export page.
  // Bumped on apply so a visible Export page refreshes its diff/changelog.
  const [f9Open, setF9Open] = useState(false);
  const [f9AppliedNonce, setF9AppliedNonce] = useState(0);

  const openUpdateSchedule = useCallback(() => setF9Open(true), []);

  const changeView = useCallback(
    (view: ViewMode) => {
      if (view === activeView) return;
      setActiveView(view);
      void trackEvent("tab_viewed", { tab: view });
    },
    [activeView],
  );

  const goToPullPlanByTaktSection = useCallback((sectionName: string) => {
    setPullPlanSeed({
      sectionName,
      nonce: Date.now(),
      mode: "plan",
      // The Takt board's per-section "Plan" button is an Adjust — edit the
      // existing sequence in place, never the wipe-and-rebuild gate.
      pullMode: "adjust",
    });
    changeView("pull-plan");
  }, [changeView]);

  useEffect(() => {
    if (!user) return;
    fetchDbVersions().then((v) => {
      setVersions(v);
      fetchActiveVersion().then((r) => setActiveVersion(r.version));
    });
  }, [user]);

  const handleVersionChange = async (version: number) => {
    await setActiveVersionApi(version);
    setActiveVersion(version);
    // Don't leave an update overlay open against the version it was launched from.
    setF9Open(false);
  };

  const handleImportComplete = (
    version: number,
    updatedVersions: DbVersionInfo[]
  ) => {
    setVersions(updatedVersions);
    setActiveVersion(version);
  };

  // Public invite-accept route. The app has no router, so we match the path
  // directly and render the claim page before the auth gate — an invitee
  // reaches it while logged out.
  const inviteMatch = window.location.pathname.match(/^\/invite\/(.+?)\/?$/);
  if (inviteMatch) {
    return <AcceptInvitePage token={decodeURIComponent(inviteMatch[1])} />;
  }

  if (loading) {
    return (
      <div className="app">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <div className="app">
      <Notice />
      <nav className="top-nav">
        <span className="nav-title">
          AutoCallahan
          <img src={process.env.PUBLIC_URL + "/callahan-logo.jpg"} alt="Callahan" className="nav-logo" />
        </span>
        <TenantSwitcher />
        <DbSwitcher
          versions={versions}
          activeVersion={activeVersion}
          onVersionChange={handleVersionChange}
          onImportComplete={handleImportComplete}
          onRenamed={(newName) =>
            setVersions((vs) =>
              vs.map((v) =>
                v.version === activeVersion
                  ? { ...v, schedule_name: newName }
                  : v
              )
            )
          }
          onDataDateSaved={(newDate) =>
            setVersions((vs) =>
              vs.map((v) =>
                v.version === activeVersion ? { ...v, data_date: newDate } : v
              )
            )
          }
        />
        <div className="nav-user">
          <span className="nav-user-name">{user.name}</span>
          <button className="nav-logout-btn" onClick={logout}>
            Sign Out
          </button>
        </div>
      </nav>

      <ViewTabs
        activeView={activeView}
        onChangeView={changeView}
        isAdmin={user.is_admin}
        rightSlot={
          <>
            <UpdateScheduleBadge activeVersion={activeVersion} onOpenUpdate={openUpdateSchedule} />
            <ProjectDeltaChip activeVersion={activeVersion} />
          </>
        }
      />

      {activeView === "admin" ? (
        <AdminPage />
      ) : versions.length === 0 ? (
        <div className="empty-state">
          <p>No schedule imported yet.</p>
          <p>Use the import button above to upload an XER file.</p>
        </div>
      ) : activeView === "export" ? (
        <ExportPage
          key={activeVersion}
          onOpenF9={openUpdateSchedule}
          f9AppliedNonce={f9AppliedNonce}
        />
      ) : activeView === "tree" ? (
        <TreeView key={activeVersion} />
      ) : activeView === "schedule" ? (
        <SchedulePage key={activeVersion} />
      ) : activeView === "pull-plan" ? (
        <PullPlanPage
          key={activeVersion}
          initialSelection={pullPlanSeed}
          onSeedConsumed={() => setPullPlanSeed(null)}
        />
      ) : (
        <TaktBoardPage
          key={activeVersion}
          onGoToPullPlanByTaktSection={goToPullPlanByTaktSection}
        />
      )}

      <UpdateScheduleHost
        activeVersion={activeVersion}
        open={f9Open}
        onClose={() => setF9Open(false)}
        onApplied={() => setF9AppliedNonce((n) => n + 1)}
      />
    </div>
  );
}

export default App;
